/**
 * ROOBAROO STUDIO (रूबरू) — Core Luxury Interactions
 * - Persistent Header Bar + Top 3D Rotating Logo (scrolls away cleanly; left menu button morphs into 3D logo inside blur circle)
 * - Card 1 Layer 2: Smooth GPU-accelerated cursor/touch reveal of compact cropped artwork mosaic
 * - Card 2 (SPOTLIGHT): Multi-image horizontal gallery navigation
 * - Card 3 (Featured Exhibits): Drag/touch scroll + multi-image dot/hover preview + direct card navigation
 * - Storewide & Dynamic Discount Pricing: Green bold discounted price, centered discount % pill below price on PDP, MutationObserver for "There's always more" recommendations
 * - Cart Drawer & Cart Page: Fixed ThemeDrawer.open(), compact shipping meter with truck at achievement point, auto-unlocked Sticker Pack with clickable thumbnail lightbox
 * - Roobaroo Gazette: Modular broadsheet modal with strict scroll-bleed prevention
 */

(function () {
  'use strict';

  const SHIPPING_THRESHOLD_INR = 999;
  const SHIPPING_THRESHOLD_CENTS = SHIPPING_THRESHOLD_INR * 100;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRoobaroo);
  } else {
    initRoobaroo();
  }

  function initRoobaroo() {
    initAnnouncementCarousel();
    initShippingMeter();
    initQuickAdd();
    initSwatches();
    initSidewaysGalleries();
    initCard3CollectionScroll();
    initSizeGuide();
    initHeroScrollAndIndicators();
    init3DLogo();
    initCategoryTabs();
    initArcadeDiscountPricing();
    initLandoHoverBanner();
    initNewspaperGazetteModal();
    initPDPZoomFix();
    interceptCartRequests();
    initMenuDrawer();
    initHeaderScroll();
    initStickerLightbox();
    initHeroExhibitionBanner();
    initSpotlightSection();
  }

  /* --------------------------------------------------------------------------
     0. Top Announcement Bar Carousel
     -------------------------------------------------------------------------- */
  function initAnnouncementCarousel() {
    const carousel = document.querySelector('[data-announcement-carousel]');
    if (!carousel) return;

    const slides = Array.from(carousel.querySelectorAll('.rbr-announcement-slide'));
    if (slides.length < 2) return;

    let currentIdx = 0;
    const showSlide = (idx) => {
      currentIdx = (idx + slides.length) % slides.length;
      slides.forEach((s, i) => s.classList.toggle('is-active', i === currentIdx));
    };

    let timer = setInterval(() => showSlide(currentIdx + 1), 4200);

    carousel.addEventListener('mouseenter', () => clearInterval(timer));
    carousel.addEventListener('mouseleave', () => {
      clearInterval(timer);
      timer = setInterval(() => showSlide(currentIdx + 1), 4200);
    });

    carousel.querySelector('[data-ann-prev]')?.addEventListener('click', () => showSlide(currentIdx - 1));
    carousel.querySelector('[data-ann-next]')?.addEventListener('click', () => showSlide(currentIdx + 1));
  }

  /* --------------------------------------------------------------------------
     1. Free Shipping Progress Meter (₹999 Threshold + Truck at Achievement Point)
     -------------------------------------------------------------------------- */
  function initShippingMeter() {
    refreshCartShipping();
  }

  async function refreshCartShipping() {
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) return;
      const cart = await res.json();
      updateMeters(cart);
    } catch (err) {
      console.warn('[Roobaroo] Failed to fetch cart state', err);
    }
  }

  function updateMeters(cart) {
    const total = cart.total_price || 0;
    const remainingCents = Math.max(0, SHIPPING_THRESHOLD_CENTS - total);
    const remainingINR = Math.ceil(remainingCents / 100);
    const rawPct = Math.min(100, Math.round((total / SHIPPING_THRESHOLD_CENTS) * 100));
    const visualPct = total > 0 ? Math.max(8, rawPct) : 0;
    const isUnlocked = total >= SHIPPING_THRESHOLD_CENTS;

    // Update PDP & Header Shipping Meters
    const pdpBoxes = document.querySelectorAll('.rbr-pdp-shipping-box, .rbr-shipping-meter-wrap');
    pdpBoxes.forEach((box) => {
      const msgElem = box.querySelector('.rbr-pdp-shipping-msg, .rbr-shipping-text');
      const barElem = box.querySelector('.rbr-shipping-progress-bar');
      if (barElem) {
        barElem.style.width = `${isUnlocked ? 100 : visualPct}%`;
        if (isUnlocked) {
          barElem.classList.add('is-unlocked');
          barElem.style.background = 'linear-gradient(90deg, #10B981, #22C55E)';
          barElem.style.boxShadow = '0 0 10px rgba(34, 197, 94, 0.65)';
        } else {
          barElem.classList.remove('is-unlocked');
          barElem.style.background = 'linear-gradient(90deg, #F59E0B, #F5C518)';
          barElem.style.boxShadow = '0 0 6px rgba(245, 197, 24, 0.35)';
        }
      }
      if (msgElem) {
        if (isUnlocked) {
          box.classList.add('is-unlocked');
          msgElem.innerHTML = '<strong class="rbr-ship-unlocked-green" style="color: #22C55E;">Free Express Shipping Unlocked!</strong>';
        } else {
          box.classList.remove('is-unlocked');
          msgElem.innerHTML = `<span>Add <strong>₹${remainingINR}</strong> more to unlock <strong>Free Express Shipping</strong></span>`;
        }
      }
    });

    // Update Cart Drawer & Cart Page Shipping Meters
    const drawerMeters = document.querySelectorAll('.rbr-drawer-shipping-meter');
    drawerMeters.forEach((drawerMeter) => {
      const labelElem = drawerMeter.querySelector('.rbr-drawer-shipping-label');
      const fillElem = drawerMeter.querySelector('.rbr-drawer-progress-fill, .rbr-shipping-progress-bar');
      const truckPath = drawerMeter.querySelector('.rbr-scale-truck-rider svg path');

      if (isUnlocked) {
        drawerMeter.classList.add('is-unlocked');
        if (fillElem) {
          fillElem.classList.add('is-unlocked');
          fillElem.style.width = '100%';
          fillElem.style.background = 'linear-gradient(90deg, #10B981, #22C55E)';
          fillElem.style.boxShadow = '0 0 12px rgba(34, 197, 94, 0.7)';
        }
        if (labelElem) {
          labelElem.innerHTML = '<strong class="rbr-ship-unlocked-green" style="color: #22C55E;">Free Express Shipping Unlocked!</strong>';
        }
        if (truckPath) {
          truckPath.setAttribute('fill', '#22C55E');
        }
      } else {
        drawerMeter.classList.remove('is-unlocked');
        if (fillElem) {
          fillElem.classList.remove('is-unlocked');
          fillElem.style.width = `${visualPct}%`;
          fillElem.style.background = 'linear-gradient(90deg, #F59E0B, #F5C518)';
          fillElem.style.boxShadow = '0 0 8px rgba(245, 197, 24, 0.4)';
        }
        if (labelElem) {
          labelElem.innerHTML = `Add <strong>₹${remainingINR}</strong> more for <strong>Free Shipping</strong>`;
        }
        if (truckPath) {
          truckPath.setAttribute('fill', '#F5C518');
        }
      }
    });

    // Update Sticker Pack Auto-Unlocked Status
    const stickerStatuses = document.querySelectorAll('[data-rbr-sticker-status]');
    stickerStatuses.forEach((statusEl) => {
      if ((cart.item_count || 0) > 0) {
        statusEl.textContent = 'Added ✓';
        statusEl.classList.add('is-unlocked');
      } else {
        statusEl.textContent = 'Free Gift';
        statusEl.classList.remove('is-unlocked');
      }
    });

    // Update Header Cart Badges
    const cartBadges = document.querySelectorAll('.rbr-cart-badge-count');
    cartBadges.forEach((badge) => {
      badge.textContent = cart.item_count || 0;
      badge.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
    });
  }

  function interceptCartRequests() {
    const originalFetch = window.fetch;
    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      try {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        if (
          url.includes('/cart/add') ||
          url.includes('/cart/change') ||
          url.includes('/cart/update') ||
          url.includes('/cart/clear')
        ) {
          setTimeout(refreshCartShipping, 250);
        }
      } catch (e) {}
      return response;
    };

    document.addEventListener('cart:updated', () => refreshCartShipping());
    document.addEventListener('cart:refresh', () => refreshCartShipping());
  }

  /* --------------------------------------------------------------------------
     2. Automated Discount Pricing (Green & Bold + PDP Centered Discount Pill + Recommendations)
     -------------------------------------------------------------------------- */
  function initArcadeDiscountPricing() {
    const runPricingUpdate = () => {
      formatNativeProductPrices();
      const savedPct = parseInt(localStorage.getItem('rbr_active_discount_pct') || '0', 10);
      if (savedPct > 0) {
        applyDiscountToAllPrices(savedPct);
      }
    };

    runPricingUpdate();

    window.addEventListener('rbr:discount-updated', (e) => {
      const pct = parseInt(e.detail?.pct || localStorage.getItem('rbr_active_discount_pct') || '0', 10);
      if (pct > 0) {
        formatNativeProductPrices();
        applyDiscountToAllPrices(pct);
      }
    });

    // Observe dynamic sections like <product-recommendations> ("There's always more..")
    if ('MutationObserver' in window) {
      let debounceTimer = null;
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        for (const m of mutations) {
          if (m.addedNodes && m.addedNodes.length > 0) {
            for (const node of m.addedNodes) {
              if (
                node.nodeType === 1 &&
                (node.matches?.('product-price, .product-card, [data-rbr-price-box]') ||
                  node.querySelector?.('product-price, .price, [data-rbr-price-box]'))
              ) {
                shouldUpdate = true;
                break;
              }
            }
          }
          if (shouldUpdate) break;
        }
        if (shouldUpdate) {
          clearTimeout(debounceTimer);
          debounceTimer = setTimeout(runPricingUpdate, 80);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  function formatNativeProductPrices() {
    // 1. Main Product Page (PDP) Price & Recommendation Cards
    const allProductPriceNodes = document.querySelectorAll('product-price');
    allProductPriceNodes.forEach((container) => {
      if (container.hasAttribute('data-rbr-price-box')) return;

      const priceEl = container.querySelector('.price');
      if (!priceEl) return;

      const compareEl = container.querySelector('.compare-at-price');
      const rawText = priceEl.textContent.trim();
      const match = rawText.match(/([\d,]+)(?:\.\d{2})?/);
      if (!match) return;

      const num = parseInt(match[1].replace(/,/g, ''), 10);
      if (isNaN(num) || num <= 0) return;

      let compareNum = 0;
      if (compareEl) {
        const cMatch = compareEl.textContent.trim().match(/([\d,]+)(?:\.\d{2})?/);
        if (cMatch) compareNum = parseInt(cMatch[1].replace(/,/g, ''), 10);
      }

      container.setAttribute('data-rbr-price-box', '');
      container.setAttribute('data-base-price', String(num));
      if (compareNum > num) {
        container.setAttribute('data-compare-price', String(compareNum));
      }

      priceEl.setAttribute('data-rbr-price-current', '');

      const isMainPDP = Boolean(container.closest('.product-information, .product-details'));
      if (isMainPDP) {
        container.classList.add('rbr-pdp-price-stack');
      }

      if (compareNum > num) {
        const nativePct = Math.round(((compareNum - num) / compareNum) * 100);
        priceEl.innerHTML = `<span class="rbr-price-compare">₹${compareNum}</span> <span class="rbr-discounted-green">₹${num}</span>`;
        if (compareEl) compareEl.style.display = 'none';

        if (isMainPDP && nativePct > 0 && !container.querySelector('.rbr-pdp-discount-pill')) {
          const pill = document.createElement('div');
          pill.className = 'rbr-pdp-discount-pill';
          pill.setAttribute('data-rbr-win-prompt', '');
          pill.textContent = `${nativePct}% OFF`;
          container.appendChild(pill);
        }
      } else {
        priceEl.textContent = `₹${num}`;
        if (isMainPDP && !container.querySelector('[data-rbr-win-prompt]')) {
          const pill = document.createElement('button');
          pill.type = 'button';
          pill.className = 'rbr-pdp-discount-pill rbr-pdp-play-pill';
          pill.setAttribute('data-open-arcade', '');
          pill.setAttribute('data-rbr-win-prompt', '');
          pill.innerHTML = 'Play &amp; Win up to 25% Off &rarr;';
          container.appendChild(pill);
        }
      }
    });
  }

  function applyDiscountToAllPrices(pct) {
    if (!pct || pct <= 0) return;

    const priceBoxes = document.querySelectorAll('[data-rbr-price-box]');
    priceBoxes.forEach((box) => {
      const baseRupees = parseInt(box.getAttribute('data-base-price') || '0', 10);
      if (!baseRupees || baseRupees <= 0) return;

      const discountedRupees = Math.round(baseRupees * (1 - pct / 100));
      const currentEl = box.querySelector('[data-rbr-price-current]');
      const parentCompare = box.querySelector(':scope > .rbr-price-compare, .rbr-price-main-wrap > .rbr-price-compare');
      if (parentCompare) parentCompare.style.display = 'none';

      if (currentEl) {
        currentEl.innerHTML = `<span class="rbr-price-compare">₹${baseRupees}</span> <span class="rbr-discounted-green">₹${discountedRupees}</span>`;
      }

      const isMainPDP = box.classList.contains('rbr-pdp-price-stack') || Boolean(box.closest('.product-information, .product-details'));
      if (isMainPDP) {
        let pill = box.querySelector('[data-rbr-win-prompt], .rbr-pdp-discount-pill');
        if (!pill) {
          pill = document.createElement('div');
          pill.setAttribute('data-rbr-win-prompt', '');
          box.appendChild(pill);
        }
        pill.className = 'rbr-pdp-discount-pill';
        pill.removeAttribute('data-open-arcade');
        pill.textContent = `${pct}% OFF`;
      }
    });
  }

  /* --------------------------------------------------------------------------
     3. Quick-Add Size Pills & Header Cart Drawer Button
     -------------------------------------------------------------------------- */
  function initQuickAdd() {
    document.addEventListener('click', async (e) => {
      const pill = e.target.closest('.rbr-size-pill');
      if (!pill) return;
      e.preventDefault();
      e.stopPropagation();

      const variantId = pill.dataset.variantId;
      if (!variantId) return;

      const originalText = pill.textContent;
      pill.textContent = '...';
      pill.style.pointerEvents = 'none';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1 }]
          })
        });

        if (res.ok) {
          pill.textContent = '✓';
          pill.style.background = '#22c55e';
          pill.style.color = '#FFFFFF';
          pill.style.borderColor = '#22c55e';
          setTimeout(() => {
            pill.textContent = originalText;
            pill.style.background = '';
            pill.style.color = '';
            pill.style.borderColor = '';
            pill.style.pointerEvents = '';
          }, 1400);

          await refreshCartDrawerSection();
          openCartDrawer();
          refreshCartShipping();
        } else {
          pill.textContent = originalText;
          pill.style.pointerEvents = '';
        }
      } catch (err) {
        pill.textContent = originalText;
        pill.style.pointerEvents = '';
      }
    });

    document.addEventListener('click', (e) => {
      if (
        e.target.closest('.rbr-cart-icon-btn') ||
        e.target.closest('.rbr-cart-pill-btn') ||
        e.target.closest('#rbr-open-cart-btn')
      ) {
        e.preventDefault();
        e.stopPropagation();
        openCartDrawer();
      }
    });
  }

  async function refreshCartDrawerSection() {
    try {
      const res = await fetch('/?sections=cart-drawer-section');
      if (!res.ok) return;
      const data = await res.json();
      if (data && data['cart-drawer-section']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(data['cart-drawer-section'], 'text/html');
        const newInner = doc.querySelector('[data-hydration-key="cart-drawer-inner"]');
        const currInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
        if (newInner && currInner) {
          currInner.innerHTML = newInner.innerHTML;
          currInner.className = newInner.className;
        }
      }
    } catch (e) {}
  }

  function openCartDrawer() {
    refreshCartShipping();
    // ThemeDrawer (#cart-drawer) defines .open(), NOT cart-drawer-component!
    const themeDrawer = document.querySelector('theme-drawer#cart-drawer') || document.getElementById('cart-drawer');
    if (themeDrawer && typeof themeDrawer.open === 'function') {
      themeDrawer.open();
      return;
    }

    const dialog = document.querySelector('#cart-drawer dialog') || document.querySelector('cart-drawer-component dialog');
    if (dialog) {
      if (typeof dialog.showModal === 'function' && !dialog.open) {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      return;
    }

    // Fallback if on /cart page where drawer is not rendered
    if (!window.location.pathname.startsWith('/cart')) {
      window.location.href = '/cart';
    }
  }

  /* --------------------------------------------------------------------------
     4. Sticker Pack Thumbnail Lightbox Popup
     -------------------------------------------------------------------------- */
  function initStickerLightbox() {
    const lightbox = document.getElementById('rbr-sticker-lightbox');
    if (!lightbox) return;

    const closeLightbox = () => {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
    };

    document.addEventListener('click', (e) => {
      const openBtn = e.target.closest('[data-sticker-preview-open]');
      if (openBtn) {
        e.preventDefault();
        e.stopPropagation();
        const imgUrl = openBtn.getAttribute('data-sticker-preview-open');
        const imgEl = document.getElementById('rbr-sticker-lightbox-img');
        if (imgEl && imgUrl) imgEl.src = imgUrl;
        lightbox.classList.add('is-open');
        lightbox.setAttribute('aria-hidden', 'false');
        return;
      }

      if (e.target.closest('[data-sticker-preview-close]')) {
        e.preventDefault();
        e.stopPropagation();
        closeLightbox();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && lightbox.classList.contains('is-open')) {
        closeLightbox();
      }
    });
  }

  /* --------------------------------------------------------------------------
     5. Variant Swatches Live Switching
     -------------------------------------------------------------------------- */
  function initSwatches() {
    document.addEventListener('click', (e) => {
      const swatch = e.target.closest('.rbr-swatch-dot');
      if (!swatch) return;
      e.preventDefault();
      e.stopPropagation();

      const card = swatch.closest('.rbr-product-card, product-card, .product-card, .product-grid__item, .resource-list__item');
      if (!card) return;

      card.querySelectorAll('.rbr-swatch-dot').forEach((s) => s.classList.remove('active'));
      swatch.classList.add('active');

      const frontImg = card.querySelector('.rbr-card-img-front')
                    || card.querySelector('.card-gallery img')
                    || card.querySelector('.product-media-container img')
                    || card.querySelector('card-gallery img')
                    || card.querySelector('img');
      const backImg = card.querySelector('.rbr-card-img-back');

      if (frontImg && swatch.dataset.frontImg) {
        frontImg.src = swatch.dataset.frontImg;
        if (frontImg.srcset) frontImg.srcset = swatch.dataset.frontImg;
      }
      if (backImg && swatch.dataset.backImg) {
        backImg.src = swatch.dataset.backImg;
        if (backImg.srcset) backImg.srcset = swatch.dataset.backImg;
      }

      // Update card link URL to select variant
      if (swatch.dataset.variantId) {
        const link = card.querySelector('a.product-card__link, a.rbr-card-title, a.rbr-card-media-wrapper');
        if (link && link.href) {
          try {
            const u = new URL(link.href, window.location.origin);
            u.searchParams.set('variant', swatch.dataset.variantId);
            link.href = u.toString();
          } catch(err) {}
        }
      }

      const mapStr = swatch.dataset.variantMap || '';
      if (mapStr) {
        const sizeMap = {};
        mapStr.split(',').forEach((pair) => {
          const [sz, vid] = pair.split(':');
          if (sz && vid) sizeMap[sz.trim()] = vid.trim();
        });
        card.querySelectorAll('.rbr-size-pill').forEach((pill) => {
          const szVal = (pill.dataset.sizeVal || pill.textContent || '').trim();
          if (sizeMap[szVal]) pill.dataset.variantId = sizeMap[szVal];
        });
      }
    });
  }

  /* --------------------------------------------------------------------------
     6. Sideways Multi-Image Gallery Controls (Card 2 — SPOTLIGHT)
     -------------------------------------------------------------------------- */
  function initSidewaysGalleries() {
    document.addEventListener('click', (e) => {
      const prevBtn = e.target.closest('[data-gallery-prev], .rbr-gallery-prev');
      const nextBtn = e.target.closest('[data-gallery-next], .rbr-gallery-next');
      if (!prevBtn && !nextBtn) return;

      e.preventDefault();
      e.stopPropagation();

      const wrap = (prevBtn || nextBtn).closest('.rbr-large-hero-card, .rbr-sideways-gallery-wrap');
      const track = wrap?.querySelector('[data-gallery-track], .rbr-large-card-gallery');
      if (!track) return;

      const slideWidth = track.clientWidth;
      const maxScroll = track.scrollWidth - track.clientWidth;

      if (nextBtn) {
        if (track.scrollLeft >= maxScroll - 20) {
          track.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: slideWidth, behavior: 'smooth' });
        }
      } else {
        if (track.scrollLeft <= 20) {
          track.scrollTo({ left: maxScroll, behavior: 'smooth' });
        } else {
          track.scrollBy({ left: -slideWidth, behavior: 'smooth' });
        }
      }
    });
  }

  /* --------------------------------------------------------------------------
     7. Card 3: Featured Exhibits Horizontal Scroll + Multi-Image Preview
     -------------------------------------------------------------------------- */
  function initCard3CollectionScroll() {
    const track = document.querySelector('[data-top5-track]');
    if (!track) return;

    // Desktop Mouse Drag to Scroll (distinguishes drag vs click so clicking card opens product page)
    let isDown = false;
    let hasMoved = false;
    let startX = 0;
    let scrollLeft = 0;

    track.addEventListener('mousedown', (e) => {
      isDown = true;
      hasMoved = false;
      track.classList.add('is-dragging');
      startX = e.pageX - track.offsetLeft;
      scrollLeft = track.scrollLeft;
    });

    track.addEventListener('mouseleave', () => {
      isDown = false;
      track.classList.remove('is-dragging');
    });

    track.addEventListener('mouseup', () => {
      isDown = false;
      track.classList.remove('is-dragging');
    });

    track.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      const x = e.pageX - track.offsetLeft;
      const walk = (x - startX) * 1.4;
      if (Math.abs(walk) > 8) {
        hasMoved = true;
        e.preventDefault();
        track.scrollLeft = scrollLeft - walk;
      }
    });

    track.addEventListener(
      'click',
      (e) => {
        if (hasMoved) {
          e.preventDefault();
          e.stopPropagation();
          hasMoved = false;
          return;
        }

        // Allow clicking image dot to switch image preview without navigating away
        const dot = e.target.closest('.rbr-top5-dot');
        if (dot) {
          e.preventDefault();
          e.stopPropagation();
          const card = dot.closest('[data-exhibit-card]');
          const idx = parseInt(dot.getAttribute('data-img-idx') || '0', 10);
          if (card) {
            const imgs = card.querySelectorAll('[data-exhibit-images] img');
            const dots = card.querySelectorAll('.rbr-top5-dot');
            imgs.forEach((img, i) => img.classList.toggle('is-active-img', i === idx));
            dots.forEach((d, i) => d.classList.toggle('is-active', i === idx));
          }
        }
      },
      true
    );

    // Desktop hover cycles to secondary image automatically
    const cards = track.querySelectorAll('[data-exhibit-card]');
    cards.forEach((card) => {
      const imgs = card.querySelectorAll('[data-exhibit-images] img');
      const dots = card.querySelectorAll('.rbr-top5-dot');
      if (imgs.length < 2) return;

      card.addEventListener('mouseenter', () => {
        imgs.forEach((img, i) => img.classList.toggle('is-active-img', i === 1));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === 1));
      });

      card.addEventListener('mouseleave', () => {
        imgs.forEach((img, i) => img.classList.toggle('is-active-img', i === 0));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === 0));
      });
    });
  }

  /* --------------------------------------------------------------------------
     8. Interactive Size Guide Modal Trigger
     -------------------------------------------------------------------------- */
  function initSizeGuide() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('.rbr-size-guide-trigger')) {
        e.preventDefault();
        document.querySelector('.rbr-modal-overlay')?.classList.add('active');
      }
      if (e.target.closest('.rbr-modal-close') || e.target.classList.contains('rbr-modal-overlay')) {
        document.querySelector('.rbr-modal-overlay')?.classList.remove('active');
      }
    });
  }

  /* --------------------------------------------------------------------------
     9. Native Reels/Shorts Scroll Indicator Dots
     -------------------------------------------------------------------------- */
  function initHeroScrollAndIndicators() {
    const slides = Array.from(document.querySelectorAll('.rbr-snap-slide'));
    const dots = Array.from(document.querySelectorAll('.rbr-snap-dot'));

    if (slides.length === 0) return;

    const heroSnapSection = document.getElementById('rbr-hero-snap-sequence') || document.getElementById('rbr-hero-snap');
    const snapIndicators = document.querySelector('.rbr-snap-indicators');
    if (heroSnapSection && snapIndicators && 'IntersectionObserver' in window) {
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            snapIndicators.classList.remove('rbr-hidden');
          } else {
            snapIndicators.classList.add('rbr-hidden');
          }
        });
      }, { threshold: 0.05 });
      sectionObserver.observe(heroSnapSection);
    }

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const idx = slides.indexOf(entry.target);
              if (idx !== -1) {
                dots.forEach((d, i) => d.classList.toggle('active', i === idx));
              }
            }
          });
        },
        { threshold: 0.45 }
      );
      slides.forEach((slide) => observer.observe(slide));
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(dot.dataset.slideIndex, 10);
        if (!isNaN(index) && slides[index]) {
          slides[index].scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     10. Interactive 3D Rotating White Logo (Header Top, Header Pill on Scroll, About Us & Footer)
     -------------------------------------------------------------------------- */
  function init3DLogo() {
    const logoStages = document.querySelectorAll('[data-rbr-3d-logo]');
    if (!logoStages.length) return;

    logoStages.forEach((logoStage) => {
      const logoSpinner =
        logoStage.querySelector(
          '.rbr-3d-logo-spinner, .rbr-header-3d-spinner, .rbr-footer-3d-spinner, .rbr-hero-center-3d-spinner, .rbr-btn-3d-logo-spinner'
        ) || logoStage.firstElementChild;
      if (!logoSpinner) return;
      const metallicCloud = logoStage.querySelector('[data-metallic-cloud]');

      let rotY = 0;
      let rotX = 5;
      let velY = 0;
      let velX = 0;
      let isInteracting = false;
      let lastX = 0;
      let lastY = 0;

      let isLogoVisible = true;
      let logoFrameId = null;

      const renderLogo = () => {
        if (!isLogoVisible) return;
        logoFrameId = requestAnimationFrame(renderLogo);
        if (!isInteracting) {
          rotY += 0.6 + velY;
          rotX += velX;
          velY *= 0.94;
          velX *= 0.94;
          rotX = Math.max(-24, Math.min(24, rotX * 0.98));
        }
        const scrollBoost = (window.scrollY || 0) * 0.1;
        logoSpinner.style.transform = `rotateX(${rotX.toFixed(1)}deg) rotateY(${(rotY + scrollBoost).toFixed(1)}deg)`;
      };

      if ('IntersectionObserver' in window) {
        const logoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              isLogoVisible = entry.isIntersecting;
              if (isLogoVisible) {
                cancelAnimationFrame(logoFrameId);
                renderLogo();
              }
            });
          },
          { rootMargin: '120px' }
        );
        logoObserver.observe(logoStage);
      } else {
        renderLogo();
      }

      // Skip drag capture on header menu button so clicking always opens the menu cleanly
      if (logoStage.closest('#rbr-open-menu-btn') || logoStage.closest('#rbr-header-top-logo-wrap')) {
        return;
      }

      const onStart = (clientX, clientY) => {
        isInteracting = true;
        lastX = clientX;
        lastY = clientY;
        velY = 0;
        velX = 0;
        logoStage.classList.add('is-interacting');
      };

      const onMove = (clientX, clientY) => {
        const rect = logoStage.getBoundingClientRect();
        const nx = (clientX - rect.left) / (rect.width || 1) - 0.5;
        const ny = (clientY - rect.top) / (rect.height || 1) - 0.5;

        if (metallicCloud) {
          const hueShift = Math.round((nx + ny) * 180);
          metallicCloud.style.transform = `translate3d(${(nx * 36).toFixed(1)}px, ${(ny * 36).toFixed(1)}px, 0) scale(1.35)`;
          metallicCloud.style.filter = `blur(22px) saturate(220%) hue-rotate(${hueShift}deg)`;
          metallicCloud.style.opacity = '1';
        }

        if (!isInteracting) return;
        const dx = clientX - lastX;
        const dy = clientY - lastY;
        rotY += dx * 0.95;
        rotX = Math.max(-45, Math.min(45, rotX - dy * 0.65));
        velY = dx * 0.65;
        velX = -dy * 0.45;
        lastX = clientX;
        lastY = clientY;
      };

      const onEnd = () => {
        isInteracting = false;
        logoStage.classList.remove('is-interacting');
        if (metallicCloud) {
          metallicCloud.style.transform = '';
          metallicCloud.style.filter = '';
          metallicCloud.style.opacity = '';
        }
      };

      logoStage.addEventListener('pointerenter', () => logoStage.classList.add('is-interacting'));
      logoStage.addEventListener('pointerdown', (e) => {
        onStart(e.clientX, e.clientY);
        try {
          logoStage.setPointerCapture(e.pointerId);
        } catch (err) {}
      });
      logoStage.addEventListener('pointermove', (e) => onMove(e.clientX, e.clientY));
      logoStage.addEventListener('pointerup', onEnd);
      logoStage.addEventListener('pointercancel', onEnd);
      logoStage.addEventListener('pointerleave', onEnd);
    });
  }

  /* --------------------------------------------------------------------------
     11. Drop Category Filter Tabs
     -------------------------------------------------------------------------- */
  function initCategoryTabs() {
    document.addEventListener('click', (e) => {
      const tab = e.target.closest('.rbr-filter-tab');
      if (!tab) return;

      const section = tab.closest('.rbr-drop-section') || document;
      section.querySelectorAll('.rbr-filter-tab').forEach((t) => t.classList.remove('active'));
      tab.classList.add('active');

      const filter = tab.dataset.filter || 'all';
      section.querySelectorAll('.rbr-product-card').forEach((card) => {
        card.style.display = filter === 'all' || card.dataset.category === filter ? 'flex' : 'none';
      });
    });
  }

  /* --------------------------------------------------------------------------
     12. Hero Banner Layer 2: Silky Smooth GPU-Accelerated Lens Reveal
     -------------------------------------------------------------------------- */
  function initLandoHoverBanner() {
    const banner = document.querySelector('[data-lando-hover-banner]');
    if (!banner) return;

    let targetX = 50;
    let targetY = 50;
    let currentX = 50;
    let currentY = 50;
    let targetR = 0;
    let currentR = 0;
    let isTouchActive = false;
    let isHoverActive = false;
    let hasUserInteracted = false;

    let isBannerVisible = true;
    let bannerFrameId = null;

    const tick = (time) => {
      if (!isBannerVisible) return;
      bannerFrameId = requestAnimationFrame(tick);

      const isMobile = window.innerWidth <= 768 || 'ontouchstart' in window;

      if (hasUserInteracted) {
        banner.classList.add('is-hovering');
        targetR = isMobile ? (isTouchActive ? 115 : 95) : isHoverActive ? 165 : 135;

        if (!isTouchActive && !isHoverActive) {
          const t = time * 0.001;
          targetX = 50 + Math.sin(t * 0.85) * 26;
          targetY = 50 + Math.cos(t * 0.65) * 18;
        }
      } else {
        targetR = 0;
      }

      // Smooth exponential interpolation (60fps silkiness)
      currentX += (targetX - currentX) * 0.14;
      currentY += (targetY - currentY) * 0.14;
      currentR += (targetR - currentR) * 0.14;

      banner.style.setProperty('--cursor-x', `${currentX.toFixed(2)}%`);
      banner.style.setProperty('--cursor-y', `${currentY.toFixed(2)}%`);
      banner.style.setProperty('--lens-r', `${currentR.toFixed(1)}px`);
    };

    if ('IntersectionObserver' in window) {
      const bannerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            isBannerVisible = entry.isIntersecting;
            if (isBannerVisible) {
              cancelAnimationFrame(bannerFrameId);
              bannerFrameId = requestAnimationFrame(tick);
            }
          });
        },
        { rootMargin: '100px' }
      );
      bannerObserver.observe(banner);
    } else {
      requestAnimationFrame(tick);
    }

    banner.addEventListener('pointerenter', () => {
      hasUserInteracted = true;
      isHoverActive = true;
      banner.classList.add('is-hovering');
    });

    banner.addEventListener('pointermove', (e) => {
      hasUserInteracted = true;
      isHoverActive = true;
      const rect = banner.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      targetX = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
      targetY = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    });

    banner.addEventListener('pointerleave', () => {
      isHoverActive = false;
    });

    banner.addEventListener(
      'touchstart',
      (e) => {
        hasUserInteracted = true;
        if (e.touches.length > 0) {
          isTouchActive = true;
          banner.classList.add('is-hovering');
          const rect = banner.getBoundingClientRect();
          targetX = Math.max(0, Math.min(100, ((e.touches[0].clientX - rect.left) / rect.width) * 100));
          targetY = Math.max(0, Math.min(100, ((e.touches[0].clientY - rect.top) / rect.height) * 100));
        }
      },
      { passive: true }
    );

    banner.addEventListener(
      'touchmove',
      (e) => {
        hasUserInteracted = true;
        if (e.touches.length > 0) {
          isTouchActive = true;
          const rect = banner.getBoundingClientRect();
          targetX = Math.max(0, Math.min(100, ((e.touches[0].clientX - rect.left) / rect.width) * 100));
          targetY = Math.max(0, Math.min(100, ((e.touches[0].clientY - rect.top) / rect.height) * 100));
        }
      },
      { passive: true }
    );

    banner.addEventListener(
      'touchend',
      () => {
        isTouchActive = false;
      },
      { passive: true }
    );
  }

  /* --------------------------------------------------------------------------
     13. Newspaper / Magazine Expandable Broadsheet Modal (Strict Scroll Containment)
     -------------------------------------------------------------------------- */
  function initNewspaperGazetteModal() {
    if (window.location.hash && window.location.hash.includes('gazette-story-')) {
      const match = window.location.hash.match(/gazette-story-([a-zA-Z0-9_\-%]+)/);
      if (match && match[1]) {
        window.location.replace('/pages/gazette?story=' + encodeURIComponent(match[1]));
        return;
      }
    }
    const fullModal = document.getElementById('rbr-gazette-full-modal');
    if (!fullModal) return;

    const scroller = fullModal.querySelector('[data-gazette-scroller], .rbr-gazette-modal-scroller') || fullModal;
    let savedScrollY = 0;

    const openFull = (storyId) => {
      savedScrollY = window.scrollY || 0;
      fullModal.classList.add('is-open');
      fullModal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('rbr-gazette-scroll-locked');
      document.body.classList.add('rbr-gazette-scroll-locked');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';

      if (storyId) {
        setTimeout(() => {
          const target =
            document.getElementById(`gazette-story-${storyId}`) ||
            fullModal.querySelector(`[data-story-handle="${storyId}"]`);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            scroller.scrollTop = 0;
          }
        }, 100);
      } else {
        scroller.scrollTop = 0;
      }
    };

    const closeFull = () => {
      fullModal.classList.remove('is-open');
      fullModal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('rbr-gazette-scroll-locked');
      document.body.classList.remove('rbr-gazette-scroll-locked');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, savedScrollY);
    };

    // Prevent scroll bleed at top/bottom boundaries of the expanded Gazette modal
    scroller.addEventListener(
      'wheel',
      (e) => {
        const atTop = scroller.scrollTop <= 0 && e.deltaY < 0;
        const atBottom = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1 && e.deltaY > 0;
        if (atTop || atBottom) {
          e.preventDefault();
        }
        e.stopPropagation();
      },
      { passive: false }
    );

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-gazette-open-full]')) {
        e.preventDefault();
        openFull();
        return;
      }

      const teaserCard = e.target.closest('[data-gazette-open-story]');
      if (teaserCard) {
        e.preventDefault();
        const storyId = teaserCard.getAttribute('data-gazette-open-story');
        openFull(storyId);
        return;
      }

      const storyLink = e.target.closest('a[href*="#gazette-story-"]');
      if (storyLink) {
        const href = storyLink.getAttribute('href');
        const hashMatch = href.match(/#gazette-story-([a-zA-Z0-9_\-%]+)/);
        if (hashMatch && hashMatch[1]) {
          const storyId = hashMatch[1];
          if (window.location.pathname === '/' || window.location.pathname === '') {
            e.preventDefault();
            openFull(storyId);
            return;
          }
        }
      }

      if (e.target.closest('[data-gazette-close-full]') || e.target === fullModal) {
        e.preventDefault();
        closeFull();
      }
    });

    // Check on load if URL has gazette hash
    if (window.location.hash && window.location.hash.includes('gazette-story-')) {
      const match = window.location.hash.match(/gazette-story-([a-zA-Z0-9_\-%]+)/);
      if (match && match[1]) {
        setTimeout(() => openFull(match[1]), 350);
      }
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && fullModal.classList.contains('is-open')) {
        closeFull();
      }
    });
  }

  /* --------------------------------------------------------------------------
     14. Smooth Clamped Pan-Zoom on Product Page (PDP)
     -------------------------------------------------------------------------- */
  function initPDPZoomFix() {
    const mediaContainers = document.querySelectorAll('.product-information__media .product-media-container');
    if (!mediaContainers.length) return;

    mediaContainers.forEach((container) => {
      const img = container.querySelector('img');
      if (!img) return;

      let scale = 1;

      const applyScale = (newScale, clientX, clientY) => {
        scale = Math.max(1, Math.min(2.15, newScale));
        if (clientX !== undefined && clientY !== undefined) {
          const rect = container.getBoundingClientRect();
          const xPct = Math.max(0, Math.min(100, ((clientX - rect.left) / (rect.width || 1)) * 100));
          const yPct = Math.max(0, Math.min(100, ((clientY - rect.top) / (rect.height || 1)) * 100));
          container.style.setProperty('--zoom-x', `${xPct.toFixed(1)}%`);
          container.style.setProperty('--zoom-y', `${yPct.toFixed(1)}%`);
        }
        img.style.transform = scale > 1.02 ? `scale(${scale.toFixed(2)})` : 'scale(1)';
        container.classList.toggle('rbr-zoom-active', scale > 1.02);
      };

      container.addEventListener(
        'wheel',
        (e) => {
          if (scale > 1.02 || e.ctrlKey) {
            e.preventDefault();
            e.stopPropagation();
            const delta = e.deltaY < 0 ? 0.22 : -0.22;
            applyScale(scale + delta, e.clientX, e.clientY);
          }
        },
        { passive: false }
      );

      container.addEventListener(
        'click',
        (e) => {
          if (e.target.closest('button')) return;
          e.preventDefault();
          e.stopPropagation();
          const nextScale = scale > 1.05 ? 1 : 1.75;
          applyScale(nextScale, e.clientX, e.clientY);
        },
        true
      );

      container.addEventListener('pointermove', (e) => {
        if (scale > 1.02) {
          applyScale(scale, e.clientX, e.clientY);
        }
      });

      container.addEventListener('pointerleave', () => {
        if (scale > 1.02) {
          applyScale(1);
        }
      });
    });
  }

  /* --------------------------------------------------------------------------
     15. Navigation Menu Drawer (Leftmost Button Always Opens Menu)
     -------------------------------------------------------------------------- */
  function initMenuDrawer() {
    const drawer = document.getElementById('rbr-menu-drawer');
    const backdrop = document.getElementById('rbr-drawer-backdrop');
    if (!drawer) return;

    const openMenu = () => {
      drawer.classList.add('is-open');
      backdrop?.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      drawer.classList.remove('is-open');
      backdrop?.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-open-roobaroo-menu], #rbr-open-menu-btn')) {
        e.preventDefault();
        e.stopPropagation();
        openMenu();
        return;
      }

      if (e.target.closest('[data-close-roobaroo-menu]')) {
        e.preventDefault();
        closeMenu();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeMenu();
      }
    });
  }

  /* --------------------------------------------------------------------------
     16. Header Scroll Dynamics:
         - Top 3D Logo scrolls away naturally (not persistent)
         - Header Bar stays persistent (fixed) across all pages (Desktop & Mobile)
         - Leftmost Menu Button morphs into 3D Rotating Logo inside blur circle on scroll
     -------------------------------------------------------------------------- */
  function initHeaderScroll() {
    const logoWrap = document.getElementById('rbr-header-top-logo-wrap');
    const navbarWrap = document.getElementById('rbr-navbar-wrapper');
    const isPDP = document.body.classList.contains('template-product') || window.location.pathname.includes('/products/');

    const onScroll = () => {
      const sy = window.scrollY || document.documentElement.scrollTop || 0;
      const isScrolled = sy > 40 || isPDP;

      document.body.classList.toggle('rbr-scrolled-header', isScrolled);
      navbarWrap?.classList.toggle('is-scrolled', isScrolled);
      logoWrap?.classList.toggle('is-scrolled-away', isScrolled);

      if (logoWrap && !isPDP) {
        const fade = Math.max(0, 1 - sy / 80);
        logoWrap.style.opacity = fade.toFixed(2);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* --------------------------------------------------------------------------
     17. Standalone Hero Exhibition Banner (3-Exhibit Switcher & Front/Back Flip)
     -------------------------------------------------------------------------- */
  function initHeroExhibitionBanner() {
    const hero = document.getElementById('rbr-hero-banner');
    if (!hero) return;

    const slides = Array.from(hero.querySelectorAll('[data-hero-exhibit-slide]'));
    const tabs = Array.from(hero.querySelectorAll('[data-hero-exhibit-btn]'));
    const codeDisplay = hero.querySelector('[data-hero-code-display]');
    const originDisplay = hero.querySelector('[data-hero-origin-display]');

    const origins = {
      '1': 'RAJPUTANA COURT FOLIO · C. 1740',
      '2': 'HOYSALA STONE MUDRA · C. 12TH C.',
      '3': 'PAHARI ROYAL ARCHIVE · C. 1780'
    };

    const activateExhibit = (numStr) => {
      slides.forEach((s) => {
        s.classList.toggle('is-active', s.getAttribute('data-hero-exhibit-slide') === String(numStr));
      });
      tabs.forEach((t) => {
        const active = t.getAttribute('data-hero-exhibit-btn') === String(numStr);
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      if (codeDisplay) codeDisplay.textContent = `EXHIBIT 0${numStr}`;
      if (originDisplay && origins[String(numStr)]) {
        originDisplay.textContent = origins[String(numStr)];
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const idx = tab.getAttribute('data-hero-exhibit-btn');
        activateExhibit(idx);
      });
    });

    hero.addEventListener('click', (e) => {
      const flipBtn = e.target.closest('[data-hero-flip-btn]');
      if (!flipBtn) return;
      e.preventDefault();
      e.stopPropagation();
      const panel = flipBtn.closest('.rbr-exhibit-tee-panel');
      if (panel) panel.classList.toggle('is-flipped');
    });

    // Gentle auto-cycle unless user is hovering over the exhibit stage
    let current = 1;
    let paused = false;
    const stage = hero.querySelector('[data-hero-exhibit-stage]');
    if (stage && slides.length > 1) {
      stage.addEventListener('pointerenter', () => { paused = true; });
      stage.addEventListener('pointerleave', () => { paused = false; });
      setInterval(() => {
        if (paused) return;
        current = (current % slides.length) + 1;
        activateExhibit(current);
      }, 6500);
    }
  }

  /* --------------------------------------------------------------------------
     18. Standalone Spotlight Product Section (Gallery, Size Pills & Instant ATC)
     -------------------------------------------------------------------------- */
  function initSpotlightSection() {
    const section = document.getElementById('rbr-spotlight-section');
    if (!section) return;

    const slides = Array.from(section.querySelectorAll('[data-spot-slide]'));
    const thumbs = Array.from(section.querySelectorAll('[data-spot-thumb]'));
    let activeIdx = 0;

    const setSlide = (idx) => {
      if (!slides.length) return;
      activeIdx = (idx + slides.length) % slides.length;
      slides.forEach((s, i) => s.classList.toggle('is-active', i === activeIdx));
      thumbs.forEach((t, i) => t.classList.toggle('is-active', i === activeIdx));
    };

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => setSlide(i));
    });

    const prevBtn = section.querySelector('[data-spot-prev]');
    const nextBtn = section.querySelector('[data-spot-next]');
    if (prevBtn) prevBtn.addEventListener('click', () => setSlide(activeIdx - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => setSlide(activeIdx + 1));

    // Size Pill Selection + Instant Add to Bag
    const sizePills = Array.from(section.querySelectorAll('[data-spot-variant-id]'));
    const atcBtn = section.querySelector('[data-spot-atc-trigger]');

    sizePills.forEach((pill) => {
      pill.addEventListener('click', () => {
        sizePills.forEach((p) => p.classList.remove('is-selected'));
        pill.classList.add('is-selected');
        const vid = pill.getAttribute('data-spot-variant-id');
        if (atcBtn && vid) {
          atcBtn.setAttribute('data-selected-variant', vid);
        }
      });
    });

    if (atcBtn) {
      atcBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = atcBtn.getAttribute('data-selected-variant');
        if (!variantId) return;

        const labelEl = atcBtn.querySelector('.rbr-spot-atc-text') || atcBtn;
        const originalText = labelEl.textContent;
        labelEl.textContent = 'Adding to Bag...';
        atcBtn.disabled = true;

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity: 1 })
          });
          if (res.ok) {
            labelEl.textContent = 'Added to Bag ✓';
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
            const cartDrawer = document.querySelector('cart-drawer-component');
            if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else {
              const headerCartBtn = document.querySelector('[data-testid="cart-drawer-trigger"], .header-actions__cart-icon');
              if (headerCartBtn) headerCartBtn.click();
            }
          } else {
            labelEl.textContent = 'Select Another Size';
          }
        } catch (err) {
          console.error('[Roobaroo Spotlight ATC]', err);
        } finally {
          setTimeout(() => {
            labelEl.textContent = originalText;
            atcBtn.disabled = false;
          }, 1600);
        }
      });
    }
  }

  // Intercept #play-to-win links in native Announcement Bar to open the Roobaroo Arcade modal
  document.addEventListener('click', (e) => {
    const playLink = e.target.closest('a[href="#play-to-win"], a[href*="#play-to-win"]');
    if (playLink) {
      e.preventDefault();
      const arcadeTrigger = document.querySelector('[data-open-arcade-modal]');
      if (arcadeTrigger) {
        arcadeTrigger.click();
      } else {
        const arcadeModal = document.getElementById('rbr-arcade-modal');
        if (arcadeModal) arcadeModal.classList.add('is-open');
      }
    }
  });

  // Re-initialize sections dynamically when edited inside Shopify Theme Customizer
  document.addEventListener('shopify:section:load', () => {
    init3DLogo();
    initHeroScrollAndIndicators();
    initSidewaysGalleries();
    initCard3CollectionScroll();
    initLandoHoverBanner();
    initHeroExhibitionBanner();
    initSpotlightSection();
    initCategoryTabs();
    initNewspaperGazetteModal();
  });
})();
